import React, { useCallback, useEffect, useState } from 'react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import adsApi from './adsApi';
import AdsImageField, { adsImageSrc } from './AdsImageField.jsx';

const emptyForm = {
  name: '',
  slug: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  website_url: '',
  logo_url: '',
  account_type: 'managed',
  status: 'active',
  notes: '',
};

export default function AdsAdvertisersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adsApi.listAdvertisers({ limit: 200 });
      setItems(res?.data?.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load advertisers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingId) await adsApi.updateAdvertiser(editingId, form);
      else await adsApi.createAdvertiser(form);
      setForm(emptyForm);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || '',
      slug: row.slug || '',
      contact_name: row.contact_name || '',
      contact_email: row.contact_email || '',
      contact_phone: row.contact_phone || '',
      website_url: row.website_url || '',
      logo_url: row.logo_url || '',
      account_type: row.account_type || 'managed',
      status: row.status || 'active',
      notes: row.notes || '',
    });
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this advertiser?')) return;
    try {
      await adsApi.deleteAdvertiser(id);
      await load();
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  if (loading) return <LoadingSpinner size="large" message="Loading advertisers..." />;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <h1 className="text-2xl font-bold text-slate-900">Advertisers</h1>
      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      <form onSubmit={onSubmit} className="grid items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-3">
        <input
          className="rounded border border-slate-300 px-3 py-2 text-sm"
          placeholder="Name *"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          required
        />
        <input
          className="rounded border border-slate-300 px-3 py-2 text-sm"
          placeholder="Slug"
          value={form.slug}
          onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
        />
        <input
          className="rounded border border-slate-300 px-3 py-2 text-sm"
          placeholder="Contact email"
          value={form.contact_email}
          onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
        />
        <input
          className="rounded border border-slate-300 px-3 py-2 text-sm"
          placeholder="Contact name"
          value={form.contact_name}
          onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
        />
        <input
          className="rounded border border-slate-300 px-3 py-2 text-sm"
          placeholder="Phone"
          value={form.contact_phone}
          onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))}
        />
        <input
          className="rounded border border-slate-300 px-3 py-2 text-sm"
          placeholder="Website URL"
          value={form.website_url}
          onChange={(e) => setForm((f) => ({ ...f, website_url: e.target.value }))}
        />
        <div className="md:col-span-3">
          <AdsImageField
            label="Logo"
            value={form.logo_url}
            onChange={(path) => setForm((f) => ({ ...f, logo_url: path }))}
            onError={(msg) => setError(msg)}
          />
        </div>
        <select
          className="rounded border border-slate-300 px-3 py-2 text-sm"
          value={form.account_type}
          onChange={(e) => setForm((f) => ({ ...f, account_type: e.target.value }))}
        >
          <option value="managed">Managed</option>
          <option value="self_service">Self-service (future)</option>
        </select>
        <select
          className="rounded border border-slate-300 px-3 py-2 text-sm"
          value={form.status}
          onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
        >
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="archived">Archived</option>
        </select>
        <div className="flex gap-2 md:col-span-3">
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {editingId ? 'Update advertiser' : 'Create advertiser'}
          </button>
          {editingId ? (
            <button
              type="button"
              className="rounded-md border border-slate-300 px-4 py-2 text-sm"
              onClick={() => {
                setEditingId(null);
                setForm(emptyForm);
              }}
            >
              Cancel
            </button>
          ) : null}
        </div>
      </form>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Logo</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-t border-slate-100">
                <td className="px-3 py-2">
                  {row.logo_url ? (
                    <img
                      src={adsImageSrc(row.logo_url)}
                      alt=""
                      className="h-8 w-8 rounded object-contain bg-slate-50"
                    />
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2 font-medium text-slate-900">{row.name}</td>
                <td className="px-3 py-2">{row.account_type}</td>
                <td className="px-3 py-2">{row.status}</td>
                <td className="px-3 py-2">{row.contact_email || '—'}</td>
                <td className="px-3 py-2 space-x-2">
                  <button type="button" className="text-blue-600 hover:underline" onClick={() => onEdit(row)}>
                    Edit
                  </button>
                  <button type="button" className="text-red-600 hover:underline" onClick={() => onDelete(row.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {!items.length ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                  No advertisers yet
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
